import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Mail, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { inputCls, labelCls } from "./shared";

export default function ParticipantsTab() {
  const [list, setList] = useState([]);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/participants")
      .then((r) => setList(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const parsed = emails.split(/[\n,;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (parsed.length === 0) return toast.error("Cole pelo menos um e-mail");
    setBusy(true);
    try {
      const res = await api.post("/admin/participants", { emails: parsed });
      toast.success(`${res.data.added.length} inscrito(s) adicionado(s). E-mail(s) de confirmação a caminho: ${res.data.emails_queued}.`);
      if (res.data.existing.length) toast.info(`${res.data.existing.length} e-mail(s) já existiam e foram ignorados.`);
      setEmails("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este inscrito?")) return;
    await api.delete(`/admin/participants/${id}`);
    toast.success("Inscrito removido");
    load();
  };

  const resend = async (id) => {
    try {
      await api.post(`/admin/participants/${id}/resend-email`);
      toast.success("E-mail reenviado");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const releaseAll = async () => {
    if (!window.confirm("Liberar certificado para TODOS os inscritos e enviar por e-mail?")) return;
    setReleasing(true);
    try {
      const res = await api.post("/admin/certificates/release");
      toast.success(`${res.data.released} certificado(s) liberado(s). E-mail(s) a caminho: ${res.data.emails_queued}.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReleasing(false); }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="card-metric space-y-3">
        <label className={labelCls}>Adicionar inscritos (um e-mail por linha, ou separados por vírgula)</label>
        <textarea value={emails} onChange={(e) => setEmails(e.target.value)} rows={4}
          className={`${inputCls} font-data text-sm`} placeholder={"ana@email.com\nbruno@email.com, carla@email.com"}
          data-testid="admin-upload-emails-input" />
        <button type="submit" disabled={busy} className="btn-primary" data-testid="admin-add-participants-btn">
          {busy ? "Enviando..." : "Adicionar e enviar confirmação"}
        </button>
      </form>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-lg font-semibold text-white">{list.length} inscrito(s)</h3>
        <button onClick={releaseAll} disabled={releasing} className="btn-primary !py-2.5 inline-flex items-center gap-2"
          data-testid="admin-release-certificates-btn">
          <Award size={15} /> {releasing ? "Liberando..." : "Liberar certificados p/ todos"}
        </button>
      </div>

      <div className="overflow-x-auto card-metric !p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-data uppercase tracking-wider text-cyan-400 border-b border-[#00C8FF]/20">
              <th className="px-4 py-3">E-mail</th>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Certificado</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-b border-white/5 text-slate-300" data-testid={`participant-row-${p.email}`}>
                <td className="px-4 py-3 font-data text-xs">{p.email}</td>
                <td className="px-4 py-3">{p.name || "—"}</td>
                <td className="px-4 py-3">
                  {p.activated
                    ? <span className="text-emerald-400 text-xs font-data uppercase">Ativo</span>
                    : <span className="text-amber-400 text-xs font-data uppercase">Pendente</span>}
                </td>
                <td className="px-4 py-3">
                  {p.certificate_released
                    ? <span className="text-[#00C8FF] text-xs font-data uppercase">Liberado</span>
                    : <span className="text-slate-500 text-xs font-data uppercase">—</span>}
                </td>
                <td className="px-4 py-3 text-right space-x-1">
                  <button onClick={() => resend(p.id)} title="Reenviar e-mail" className="p-1.5 text-cyan-400 hover:bg-cyan-500/10 rounded" data-testid={`resend-email-${p.email}`}>
                    <Mail size={14} />
                  </button>
                  <button onClick={() => remove(p.id)} title="Remover" className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" data-testid={`delete-participant-${p.email}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Nenhum inscrito ainda. Cole os e-mails acima.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
